import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { paths } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";
import { textareaClass } from "./JobEditCard";

// Templates → On your documents: the phone, email and license numbers
// printed under the company's name on estimates, invoices and agreements.
// A logo that already carries the license numbers switches the print off.

export function CompanyDetailsCard({ tenantId }: { tenantId: string }) {
  const [form, setForm] = useState({ phone: "", email: "", licenseLines: "", print: true });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  useEffect(
    () =>
      onSnapshot(doc(db, paths.tenant(tenantId)), (s) => {
        if (dirty) return;
        setForm({
          phone: (s.get("contactPhone") as string) ?? "",
          email: (s.get("contactEmail") as string) ?? "",
          licenseLines: (s.get("licenseLines") as string) ?? "",
          print: s.get("printLicenseLines") !== false,
        });
      }),
    [tenantId, dirty],
  );
  const set = (patch: Partial<typeof form>) => {
    setDirty(true);
    setForm((f) => ({ ...f, ...patch }));
  };
  async function save() {
    setBusy(true);
    setNote("");
    try {
      await call("setCompanyDetails", { tenantId, contactPhone: form.phone.trim(), contactEmail: form.email.trim(), licenseLines: form.licenseLines.trim(), printLicenseLines: form.print });
      setDirty(false);
      setNote("Saved. New PDFs show it from now on.");
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="space-y-3">
      <CardTitle className="mb-0">On your documents</CardTitle>
      <p className="text-xs text-steel">Printed under your company name on estimates, invoices and agreements, and shown on the pages customers sign from.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Phone</Label>
          <Input value={form.phone} maxLength={40} placeholder="702-555-0100" onChange={(e) => set({ phone: e.target.value })} />
        </div>
        <div>
          <Label>Email</Label>
          <Input type="email" value={form.email} maxLength={200} placeholder="office@yourcompany.com" onChange={(e) => set({ email: e.target.value })} />
        </div>
      </div>
      <div>
        <Label>License numbers and limits</Label>
        <textarea
          className={cn(textareaClass, "min-h-14")}
          value={form.licenseLines}
          maxLength={300}
          placeholder={"NSC License #'s 52360, 53252 · Unlimited\nAnother line if you need one"}
          onChange={(e) => set({ licenseLines: e.target.value })}
        />
        <label className="mt-1 flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={form.print} onChange={(e) => set({ print: e.target.checked })} />
          <span className="text-ink">Print them on documents</span>
        </label>
        <p className="text-[11px] text-steel">Turn the print off if your logo already carries the license numbers, so they don't show twice. Many states require a contractor's license number on contracts; check yours.</p>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" busy={busy} disabled={!dirty} onClick={() => void save()}>
          Save
        </Button>
        {note ? <span className="text-xs text-steel">{note}</span> : null}
      </div>
    </Card>
  );
}
